"use client";

import { useCallback } from "react";
import Flex from "~/components/flex/Flex";
import { Icon, IconName } from "~/components/icons/Icon";
import Text from "~/components/texts/Text";
import { useI18n } from "~/locales/client";
import { AttachmentType } from "~/types-and-enums/purchaseOrderTypes";
import { getMimeTypesMap } from "~/utils/generalUtils";
import { spacing } from "~/utils/tailwindUtils";

type UploadedFilesListProps = {
  attachments: AttachmentType[] | string[]; // Support both old format (string[]) and new format (AttachmentType[])
};

/**
 * Component to display already uploaded files from backend (read-only with download)
 */
const UploadedFilesList = ({ attachments }: UploadedFilesListProps) => {
  const translate = useI18n();

  /**
   * Download file from base64 data
   */
  const handleDownload = useCallback((attachment: AttachmentType) => {
    try {
      // Decode base64 to binary
      const binaryString = atob(attachment.version_data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob from binary data
      const mimeType = attachment.file_extension
        ? getMimeTypesMap(attachment.file_extension)
        : "application/octet-stream";
      const blob = new Blob([bytes], { type: mimeType });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.title || attachment.path_on_client || `file.${attachment.file_extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  }, []);

  // Handle backward compatibility: if attachments is string[], return early
  if (!attachments || attachments.length === 0) return null;

  // New format - AttachmentType[]
  const attachmentList = attachments as AttachmentType[];

  return (
    <Flex className="flex-col gap-2">
      <Text color="lightGray" fontWeight="semibold" className="text-[8px] md:text-xs">
        {translate("general.uploaded-files")}
      </Text>
      <ul className="space-y-2">
        {attachmentList.map((attachment, index) => {
          const fileName = attachment.title || attachment.path_on_client || `file.${attachment.file_extension}`;
          const fileSizeKB = Math.round(attachment.content_size / 1024).toFixed(0);

          return (
            <Flex
              key={attachment.id || `${attachment.title}-${index}`}
              className="gap-2 py-1 border-b border-gray-100 justify-between items-center"
            >
              <Flex className="gap-x-4">
                <Icon name={IconName.Document} size={spacing[4]} />
                <li className="text-sm font-medium truncate max-w-60 md:max-w-50 lg:max-w-70 text-wrap">{fileName}</li>
              </Flex>
              <Flex className="gap-x-4">
                {attachment.content_size && (
                  <Flex className="px-2 py-1 bg-gray-100 rounded-full">
                    <Text color="lightGray" className="text-xs min-w-max">
                      {fileSizeKB} KB
                    </Text>
                  </Flex>
                )}
                <Icon
                  name={IconName.Download}
                  size={spacing[4]}
                  onClick={() => handleDownload(attachment)}
                  className="cursor-pointer hover:opacity-70"
                />
              </Flex>
            </Flex>
          );
        })}
      </ul>
    </Flex>
  );
};

export default UploadedFilesList;
